/* eslint-disable no-unused-vars */
import React, { Children, Component, Fragment } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  Row,
  Col,
  Container,
  Input,
  Button,
  ModalFooter,
} from 'reactstrap';
import PropTypes from 'prop-types';
import { fromJS } from 'immutable';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { resolveDisplayName } from './resultGridUtils';
import { toggleColumn } from '../../util/sortUtils';
import { IconWithTooltip } from '../icon/IconWithTooltip';

const renderCheckbox = (column, selected, sortedIds, handleColumnToggle, handleSortAdd) => {
  const { id, alwaysVisible, sortable } = column;
  const name = resolveDisplayName(column);
  return (
    <Fragment key={`visible-${id}`}>
      <Row className="haz-container-grid-row" key={id}>
        <Col xs="8" key={`name-${id}`}>{name}</Col>
        <Col xs="2" key={`selected-${id}`}>
          <Input
            type="checkbox"
            className="haz-unlabel-checkbox"
            checked={alwaysVisible || selected.includes(id)}
            onChange={e => handleColumnToggle(id, e)}
            aria-label={name}
            disabled={alwaysVisible}
          />
        </Col>
        <Col xs="2" key={`sort-${id}`}>
          {sortable && !sortedIds.includes(id) && (
          <IconWithTooltip
            handleClick={() => handleSortAdd(id, name)}
            ariaLabel="sortBy"
            tooltip="Sorted By"
            icon="plus-circle"
            className="haz-add-option-button mr-3"
            size="lg"
          />
          )}
        </Col>
      </Row>
    </Fragment>
  );
};

const renderSortTile = (column, index, size, handleSortChange, handleSortRemove) => {
  const asc = column.get('asc');
  const id = column.get('id');
  const name = column.get('name');
  return (
    <Fragment key={`sort-${id}`}>
      <Row className="haz-container-grid-row" key={id}>
        <Col xs="7" key={`name-${id}`}>{name}</Col>
        <Col xs="1" className="text-center" key={`asc-${id}`}>
          <Input
            type="checkbox"
            checked={asc}
            onChange={() => handleSortChange(column.set('asc', !asc), 0)}
            className="haz-unlabel-checkbox"
            aria-label={name}
          />
        </Col>
        <Col xs="2" key={`direction-${id}`}>
          <Row>
            <Col xs="6" className="haz-arrow-col text-center no-gutters">
              {index < size - 1 && (
              <IconWithTooltip
                handleClick={() => handleSortChange(column, 1)}
                ariaLabel="moveDown"
                tooltip="Move Down"
                icon="angle-down"
                size="lg"
              />
              )}

            </Col>
            <Col xs="6" className="haz-arrow-col  text-left no-gutters">
              { index > 0 && (
              <IconWithTooltip
                handleClick={() => handleSortChange(column, -1)}
                ariaLabel="moveUp"
                tooltip="Move Up"
                icon="angle-up"
                size="lg"
              />
              )}

            </Col>
          </Row>
        </Col>
        <Col xs="2" key={`remove-${id}`}>
          <IconWithTooltip
            handleClick={() => handleSortRemove(id)}
            ariaLabel="remove"
            tooltip="Remove"
            icon="minus-circle"
            className="haz-add-option-button mr-3"
            size="lg"
          />
        </Col>
      </Row>
    </Fragment>
  );
};

export class ResultGridColumnModal extends Component {
  constructor(props) {
    super(props);
    this.handleClose = this.handleClose.bind(this);
    this.handleColumnToggle = this.handleColumnToggle.bind(this);
    this.setVisibleColumns = this.setVisibleColumns.bind(this);
    this.handleSortAdd = this.handleSortAdd.bind(this);
    this.handleSortRemove = this.handleSortRemove.bind(this);
    this.handleSortChange = this.handleSortChange.bind(this);
    this.handleAllColumns = this.handleAllColumns.bind(this);
    this.handleDefaultColumns = this.handleDefaultColumns.bind(this);
    this.handleDeselectAllColumns = this.handleDeselectAllColumns.bind(this);
    // sort shape = {id: id, asc: true}
    this.state = {
      selected: fromJS([]),
      sort: fromJS([]),
    };
  }

  componentDidMount() {
    this.setVisibleColumns();
  }

  componentDidUpdate({ isOpen: wasOpen }) {
    const { isOpen } = this.props;
    if (isOpen && !wasOpen) {
      this.setVisibleColumns();
    }
  }

  setVisibleColumns() {
    const { visibleColumns, sort, children } = this.props;

    const map = {};
    children.filter(o => o).forEach((child) => {
      map[child.props.id] = resolveDisplayName(child.props);
    });

    this.setState({
      selected: visibleColumns,
      sort: sort.map(sortObj => fromJS({ id: sortObj.get('id'), asc: sortObj.get('asc'), name: map[sortObj.get('id')] })),
    });
  }

  handleAllColumns() {
    const { children } = this.props;
    const newSelected = [];
    children.filter(o => o).forEach((child) => {
      newSelected.push(child.props.id);
    });

    this.setState({ selected: fromJS(newSelected) });
  }


  handleDefaultColumns() {
    const { defaultColumns, defaultSort, children } = this.props;

    const map = {};
    children.filter(o => o).forEach((child) => {
      map[child.props.id] = resolveDisplayName(child.props);
    });

    this.setState({
      selected: defaultColumns,
      sort: defaultSort.map(sortObj => fromJS({ id: sortObj.get('id'), asc: sortObj.get('asc'), name: map[sortObj.get('id')] })),
    });
  }

  handleDeselectAllColumns() {
    const { minColumns } = this.props;
    this.setState({ selected: minColumns });
  }

  handleColumnToggle(id) {
    this.setState(({ selected }) => ({ selected: toggleColumn(selected, id) }));
  }

  handleSortAdd(id, name) {
    this.setState(({ sort }) => ({ sort: sort.push(fromJS({ id, name, asc: true })) }));
  }

  handleSortRemove(id) {
    this.setState(({ sort }) => ({ sort: sort.filter(sortObj => sortObj.get('id') !== id) }));
  }

  handleSortChange(changeSort, direction) {
    this.setState(({ sort }) => {
      const preIndex = sort.findIndex(sortObj => sortObj.get('id') === changeSort.get('id'));
      if (preIndex > -1) {
        if (direction === 0) {
          return { sort: sort.set(preIndex, changeSort) };
        }
        const newSort = sort.delete(preIndex);
        return { sort: newSort.insert(preIndex + direction, changeSort) };
      }
      return { sort };
    });
  }

  handleClose() {
    const { handleColumnsToggle, toggleOpen } = this.props;
    const { selected, sort } = this.state;
    const normalizedSort = sort.map(sortObj => sortObj.delete('name'));
    this.setState({
      selected: fromJS([]),
      sort: fromJS([]),
    });

    handleColumnsToggle({ visibleColumns: selected, sort: normalizedSort });
    toggleOpen();
  }

  render() {
    const {
      children, isOpen, toggleOpen, minColumns,
    } = this.props;
    const { selected, sort } = this.state;
    const sortedIds = sort.map(column => column.get('id'));
    return (
      <Modal className="haz-sort-modal" isOpen={isOpen} toggle={toggleOpen}>
        <ModalHeader>
          Select and Sort Columns
        </ModalHeader>
        <ModalBody>
          <Container>
            <Row>
              <Col className="haz-table-container" lg="6">
                <Row className="haz-container-title">
                  <Col><h5 className="text-left">Visible Columns</h5></Col>
                  <Col className="haz-visible-column-buttons">
                    <div>
                      <Button
                        outline
                        color="secondary"
                        size="sm"
                        onClick={() => this.handleAllColumns()}
                      >
                        Select All
                      </Button>
                      {' '}
                      { minColumns.size > 0 && (
                      <Button
                        outline
                        color="secondary"
                        size="sm"
                        onClick={() => this.handleDeselectAllColumns()}
                      >
                        Deselect All
                      </Button>
                      )}
                      {' '}
                      <Button
                        outline
                        color="secondary"
                        size="sm"
                        onClick={() => this.handleDefaultColumns()}
                      >
                        Default
                      </Button>
                    </div>
                  </Col>
                </Row>
                <Row className="haz-table-container-label">
                  <Col xs="8"><h6 className="text-left">Column</h6></Col>
                  <Col xs="2"><h6 className="text-left">Visible</h6></Col>
                  <Col xs="2"><h6 className="text-left">Sort</h6></Col>
                </Row>
                <Container className="haz-container-grid">
                  {
                    Children.toArray(children)
                      .filter(o => o)
                      .map(({ props }) => renderCheckbox(props, selected, sortedIds, this.handleColumnToggle, this.handleSortAdd))
                   }
                </Container>
              </Col>
              <Col className="haz-table-container" lg="6">
                <Row className="haz-container-title">
                  <Col><h5 className="text-left">Sort Order</h5></Col>
                </Row>
                <Row className="haz-table-container-label">
                  <Col xs="7"><h6 className="text-left">Column</h6></Col>
                  <Col xs="1"><h6 className="text-left">Asc</h6></Col>
                  <Col xs="2"><h6 className="text-left no-gutters">Order</h6></Col>
                  <Col xs="2"><h6 className="text-left no-gutters">Remove</h6></Col>
                </Row>
                <Container className="haz-container-grid" xs="4">
                  {
                    sort.map((column, index) => renderSortTile(column, index, sort.size, this.handleSortChange, this.handleSortRemove))
                   }
                </Container>
              </Col>
            </Row>
          </Container>
        </ModalBody>
        <ModalFooter className="haz-sort-modal-buttons">
          <Button
            outline
            color="primary"
            size="sm"
            onClick={this.handleClose}
          >
            Accept
          </Button>
          {' '}
          <Button
            outline
            color="primary"
            size="sm"
            onClick={toggleOpen}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    );
  }
}


ResultGridColumnModal.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.element), PropTypes.element, PropTypes.node]).isRequired,
  visibleColumns: ImmutablePropTypes.list.isRequired,
  isOpen: PropTypes.bool.isRequired,
  toggleOpen: PropTypes.func.isRequired,
  handleColumnsToggle: PropTypes.func.isRequired,
  sort: ImmutablePropTypes.list.isRequired,
  defaultColumns: ImmutablePropTypes.list.isRequired,
  defaultSort: ImmutablePropTypes.list.isRequired,
  minColumns: ImmutablePropTypes.list.isRequired,
};
